/** Same-origin Better Auth session and official Polar plugin helpers. */
export type AuthState = {
  user_id: string | null;
  email: string | null;
  is_paid: boolean;
  plan: "monthly" | "annual" | "pro" | null;
  loading: boolean;
};

export const EMPTY_AUTH: AuthState = {
  user_id: null,
  email: null,
  is_paid: false,
  plan: null,
  loading: true,
};

export async function getAuthState(): Promise<AuthState> {
  try {
    const sessionResponse = await fetch("/api/auth/get-session", {
      credentials: "include",
      cache: "no-store",
    });
    if (!sessionResponse.ok) return { ...EMPTY_AUTH, loading: false };

    const session = await sessionResponse.json();
    if (!session?.user?.id) return { ...EMPTY_AUTH, loading: false };

    const paidResponse = await fetch("/api/auth/me", {
      credentials: "include",
      cache: "no-store",
    });
    const paid = paidResponse.ok ? await paidResponse.json() : { is_paid: false };
    return {
      user_id: session.user.id,
      email: session.user.email || null,
      is_paid: !!paid.is_paid,
      plan: paid.plan || null,
      loading: false,
    };
  } catch {
    return { ...EMPTY_AUTH, loading: false };
  }
}

export async function signInEmail(email: string, password: string, mode: "signin" | "signup"): Promise<void> {
  const path = mode === "signup" ? "/api/auth/sign-up/email" : "/api/auth/sign-in/email";
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password, name: email.split("@")[0] }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.message || payload?.error || "We couldn't sign you in. Please check your details.");
  }
  window.dispatchEvent(new Event("rrp:auth"));
}

export async function signOut(): Promise<void> {
  const response = await fetch("/api/auth/sign-out", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.message || payload?.error || "We couldn't sign you out. Please try again.");
  }
}

async function polarAction(path: string, body: Record<string, unknown> = {}): Promise<void> {
  const response = await fetch(`/api/auth/${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ...body, redirect: false }),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.url) {
    throw new Error(payload?.message || payload?.error || "Polar is temporarily unavailable. Please try again.");
  }
  window.location.assign(payload.url);
}

export function startPolarCheckout(plan: "monthly" | "annual"): Promise<void> {
  return polarAction("checkout", { slug: plan });
}

export function openPolarPortal(): Promise<void> {
  return polarAction("customer/portal");
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, { ...init, credentials: "include" });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`${path}: ${response.status} ${text}`);
  }
  return response.json();
}
